import React, { useEffect, useMemo, useState } from "react";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import "./PhoneNumberField.css";
import useCountryCodes from "../../hooks/useCountryCodes";

const DEFAULT_DIAL_CODE = "1"; // US

const PhoneNumberField = ({
  label = "Phone Number",
  phoneNumber = "",
  onChange,
  error = false,
  helperText = "",
  disabled = false,
  required = false,
  className = "",
}) => {
  const { countries, loading } = useCountryCodes();

  const [internalError, setInternalError] = useState("");
  const [focused, setFocused] = useState(false);

  /*
   * Convert stored phone number into:
   *
   * 91-9876543210
   *
   * countryCode = 91
   * localNumber = 9876543210
   */
  const parsedPhone = useMemo(() => {
    if (!phoneNumber) {
      return {
        countryCode: "",
        localNumber: "",
      };
    }

    /*
     * If an old/existing number doesn't contain "-",
     * treat it as a US number for backward compatibility.
     */
    if (!phoneNumber.includes("-")) {
      return {
        countryCode: DEFAULT_DIAL_CODE,
        localNumber: phoneNumber,
      };
    }

    const [countryCode, ...numberParts] = phoneNumber.split("-");

    return {
      countryCode,
      localNumber: numberParts.join("-"),
    };
  }, [phoneNumber]);

  /*
   * Find the country from our API using the stored dial code.
   */
  const selectedCountry = useMemo(() => {
    if (!parsedPhone.countryCode || !countries.length) {
      return null;
    }

    const storedDialCode = parsedPhone.countryCode.replace(/\D/g, "");

    return countries.find(
      (country) =>
        String(country.dialCode).replace(/\D/g, "") === storedDialCode
    );
  }, [countries, parsedPhone.countryCode]);

  /*
   * Country used by react-phone-input-2.
   *
   * If there is no stored phone number, default to US.
   */
  const defaultCountry =
    selectedCountry?.isO2?.toLowerCase() || "us";

  /*
   * Remount PhoneInput only when the user is not typing.
   * This allows the country to update when API data loads
   * without losing focus on every keystroke.
   */
  const [mountKey, setMountKey] = useState(defaultCountry);

  useEffect(() => {
    if (!focused) {
      setMountKey(defaultCountry);
    }
  }, [defaultCountry, focused]);

  /*
   * react-phone-input-2 expects:
   *
   * 19876543210
   *
   * Our application stores:
   *
   * 1-9876543210
   *
   * Therefore remove the "-" before passing the value
   * to PhoneInput.
   */
  const phoneValue = useMemo(() => {
    if (!parsedPhone.countryCode && !parsedPhone.localNumber) {
      return "";
    }

    return `${parsedPhone.countryCode || ""}${
      parsedPhone.localNumber || ""
    }`;
  }, [parsedPhone]);

  /*
   * Handle phone number / country changes.
   */
  const handleChange = (value, countryData) => {
    const selected = countries.find(
      (country) =>
        country.isO2?.toLowerCase() ===
        countryData.countryCode?.toLowerCase()
    );

    /*
     * If our API doesn't contain the selected country,
     * return the raw value instead of breaking the field.
     */
    if (!selected) {
      setInternalError("");

      onChange?.({
        phoneNumber: value,
      });

      return;
    }

    const dialCode = String(selected.dialCode).replace(/\D/g, "");

    let localPhoneNumber = value;

    /*
     * Remove the country dial code from the value.
     *
     * Example:
     * value = 919876543210
     * dialCode = 91
     *
     * localPhoneNumber = 9876543210
     */
    if (dialCode && value.startsWith(dialCode)) {
      localPhoneNumber = value.substring(dialCode.length);
    }

    /*
     * Store in our application format:
     *
     * 91-9876543210
     *
     * If the user removes everything, store an empty string.
     */
    const formattedPhoneNumber =
      localPhoneNumber.trim() === ""
        ? ""
        : dialCode
          ? `${dialCode}-${localPhoneNumber}`
          : localPhoneNumber;

    setInternalError("");

    onChange?.({
      phoneNumber: formattedPhoneNumber,
    });
  };

  /*
   * MUI-style floating label:
   * label moves to the top when focused OR when a value exists.
   */
  const shouldShrinkLabel = focused || !!phoneValue;

  return (
    <div
      className={`phone-number-field mui-like-field ${className} ${
        error ? "phone-error" : ""
      } ${shouldShrinkLabel ? "label-shrink" : ""} ${
        focused ? "field-focused" : ""
      } ${disabled ? "field-disabled" : ""}`}
    >
      <label
        className={`mui-like-label ${
          shouldShrinkLabel ? "shrink" : ""
        }`}
      >
        {label}
        {required && " *"}
      </label>

      <PhoneInput
        country={defaultCountry}
        key={mountKey}
        value={phoneValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder=""
        enableSearch
        disableSearchIcon
        countryCodeEditable={false}
        disabled={disabled || loading}
        disableCountryGuess
        searchPlaceholder="Search"
        searchNotFound="No country found"
        inputProps={{
          required,
          name: "phoneNumber",
          type: "tel",
        }}
        containerClass="phone-input-container"
        inputClass="phone-input"
        buttonClass="phone-input-button"
        dropdownClass="phone-input-dropdown"
      />

      {(helperText || internalError) && (
        <div
          className={`phone-helper-text ${
            error || internalError ? "phone-error" : ""
          }`}
        >
          {helperText || internalError}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberField;